import { Node } from '@tiptap/pm/model'
import { Plugin, TextSelection } from '@tiptap/pm/state'
import { EditorView } from '@tiptap/pm/view'
import { Editor, Extension, ReactRenderer } from '@tiptap/react'
import { TbPencilBolt } from 'react-icons/tb'

import { CREATE_FOCUS_EVENT_NAME, emitter } from '@/components/editor/events'

export interface AiBlockOptions {
  editor: Editor
  speed: number
  position:
    | 'follow'
    | 'center'
    | 'top'
    | 'bottom'
    | ((nodeRect: DOMRect, handleRect: DOMRect) => number)
  createAiBlockButtonHTMLElement: (editor: Editor) => HTMLElement
}

const AiBlockButton: React.FC = () => {
  return (
    <TbPencilBolt className="w-5 h-5 text-primary-500 dark:text-primary-400 stroke-[1.5px] hover:opacity-70" />
  )
}

const createAiBlockButton = (editor: Editor) => {
  const button = new ReactRenderer(AiBlockButton, {
    editor,
    className:
      'fixed top-0 left-2 will-change-transform transition-opacity ease-in-out text-muted-foreground rounded opacity-0',
    as: 'button',
  })

  const htmlElement = button.element as HTMLElement
  return htmlElement
}

const getElementAndNodePos = (
  view: EditorView,
  coords: { left: number; top: number } = { left: 0, top: 0 },
): {
  element: Element
  pos: number
  node: Node | null | undefined
} | null => {
  try {
    const bounds = view.dom.getBoundingClientRect()

    const nodePos = view.posAtCoords({
      top: Math.min(bounds.bottom, Math.max(bounds.top + 1, coords.top)),
      // offset of 60 to account for the drag handle
      left: Math.min(bounds.right, Math.max(bounds.left + 1, coords.left + 60)),
    })?.inside
    if (nodePos == null || nodePos < 0) {
      return null
    }

    const node = view.state.doc.nodeAt(nodePos) // get the ProseMirror node at the calculated position
    const domNode = view.nodeDOM(nodePos)

    if (
      !domNode ||
      !(domNode instanceof Element) ||
      !node ||
      node.content.size === 0
    ) {
      return null
    }

    return {
      element: domNode,
      pos: nodePos,
      node: node,
    }
  } catch (e) {
    return null
  }
}

const AiBlockPlugin = (options: AiBlockOptions) => {
  let handle: HTMLElement | null = null

  let targetLeft: number
  let prevTop: number
  let targetTop: number

  requestAnimationFrame(function animation() {
    if (handle) {
      const distanceTop = targetTop - prevTop
      const nextTop = prevTop + distanceTop

      handle.style.transform = `translate3d(calc(-100% - 0.5rem + ${targetLeft}px), ${nextTop}px, 0)`
      prevTop = nextTop
    }

    requestAnimationFrame(animation)
  })

  const createMouseMoveHandler = (view: EditorView) => (event: MouseEvent) => {
    if (!view.editable || !handle) {
      return
    }

    const data = getElementAndNodePos(view, {
      left: event.clientX,
      top: event.clientY,
    })

    if (!data) {
      hideDragHandle()
      return
    }

    const { node } = data

    showDragHandle()

    const rect = data.element.getBoundingClientRect()
    const handleRect = handle.getBoundingClientRect()

    // menu follows cursor
    if (options.position === 'follow') {
      targetTop = Math.min(
        rect.bottom - handleRect.height,
        Math.max(rect.top, event.clientY - handleRect.height / 2),
      )
    }

    // menu centers on node
    else if (options.position === 'center') {
      targetTop = (rect.top + rect.bottom) / 2 - handleRect.height / 2
    }

    // menu anchors to top of node
    else if (options.position === 'top') {
      let offset = 0
      if (node && node.type.name === 'heading') {
        switch (node.attrs.level) {
          case 1:
            offset = 8
            break
          case 2:
            offset = 5
            break
          case 3:
            offset = 4
          default:
            break
        }
      }
      targetTop = rect.top + offset
    }

    // menu anchors to bottom of node
    else if (options.position === 'bottom') {
      targetTop = rect.bottom - handleRect.height
    }

    // custom menu position
    else {
      targetTop = options.position(rect, handleRect)
    }

    targetLeft = view.dom.getBoundingClientRect().left

    showDragHandle()
  }

  const createClickHandler = (view: EditorView) => (event: MouseEvent) => {
    view.focus()

    const data = getElementAndNodePos(view, {
      left: event.clientX,
      top: event.clientY,
    })

    if (!data) {
      return
    }

    const { node, pos } = data
    if (!node) {
      return
    }

    emitter.emit(CREATE_FOCUS_EVENT_NAME, {
      // for some reason, we need to focus on the text node inside the heading,
      // or else the text above the heading gets cut off when removing the focus
      from: node.type.name !== 'heading' ? pos : pos + 2,
      to: pos + node.nodeSize,
    })
  }

  const hideDragHandle = () => handle?.classList.add('opacity-0')
  const showDragHandle = () => handle?.classList.remove('opacity-0')

  return new Plugin({
    view: (view) => {
      const parent = view.dom.parentElement

      handle = options.createAiBlockButtonHTMLElement(options.editor)

      const clickHandler = createClickHandler(view)
      const mouseMoveHandler = createMouseMoveHandler(view)

      handle.addEventListener('click', clickHandler)
      parent?.addEventListener('mousemove', mouseMoveHandler)
      document?.addEventListener('scroll', hideDragHandle, true)

      const { left = 0, top = 0 } =
        getElementAndNodePos(view)?.element.getBoundingClientRect() ?? {}

      targetLeft = left
      // oxlint-disable-next-line no-multi-assign
      prevTop = targetTop = top

      parent?.appendChild(handle)

      return {
        destroy: () => {
          handle?.removeEventListener('click', clickHandler)
          parent?.removeEventListener('mousemove', mouseMoveHandler)
          document?.removeEventListener('scroll', hideDragHandle, true)
          handle?.remove?.()
          handle = null
        },
      }
    },
    props: {
      handleDOMEvents: {
        keydown: () => hideDragHandle(),
        dragstart: (view) => view.dom.classList.add('dragging'),
        drop: (view) => view.dom.classList.remove('dragging'),
        dragend: (view) => view.dom.classList.remove('dragging'),
      },
    },
  })
}

export const AiBlock = Extension.create<Omit<AiBlockOptions, 'editor'>>({
  name: 'aiBlock',

  addOptions() {
    return {
      speed: 3,
      position: 'follow',

      createAiBlockButtonHTMLElement(editor: Editor) {
        const handle = createAiBlockButton(editor)
        return handle
      },
    }
  },

  addProseMirrorPlugins() {
    return [
      AiBlockPlugin({
        editor: this.editor as Editor,
        ...this.options,
      }),
    ]
  },
})
